import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlatformAuditArea, PlatformAuditEntry, listPlatformAudit } from '../../api/admin';
import { TableCard } from '../../components/fs';
import { PHONE, useMediaQuery } from '../../shared/media';
import { PLATFORM_ROLE_LABEL, PlatformRole } from '../../shared/types';
import { localDay, money } from './planDisplay';

// ---------------------------------------------------------------------------
// The platform audit trail (R12: its own page, "Audit · Platform audit log",
// plus the "Recent platform activity" section the Accounts page keeps).
//
// Read-only on purpose, and it is the whole point: an append-only record that
// the console can edit is not a record. Everything here renders what the
// server stored, including the account and staff names it copied at write
// time — so an entry about a deleted customer still says which customer.
// ---------------------------------------------------------------------------

/** Who did it, as a trail names them: the jobs (the purge, the free-period sweep) are "System". */
export const actorName = (email: string) => (email === 'system' ? 'System' : email);

export const ACTION_LABEL: Record<string, string> = {
  'account.create': 'Account created',
  'account.rename': 'Renamed',
  'account.status': 'Status',
  'account.plan': 'Plan',
  'account.billing': 'Plan & pricing',
  'account.price': 'Agreed price',
  'account.free_period': 'Free period',
  'account.credit': 'Credit',
  'account.discount': 'Percent off',
  'account.billing_email': 'Billing email',
  'account.trial_grant': 'Free upgrade granted',
  'account.trial_update': 'Free upgrade changed',
  'account.trial_revoke': 'Free upgrade revoked',
  'account.trial_end': 'Free upgrade ended',
  'account.moved_to_free': 'Moved to Free',
  'account.portal_link': 'Portal link sent',
  'account.reconciled': 'Matched to Stripe',
  'account.delete': 'Account deleted',
  'account.delete_scheduled': 'Deletion scheduled',
  'account.restore': 'Restored',
  'account.note': 'Staff note',
  'account.user_add': 'Login added',
  'staff.create': 'Staff hired',
  'staff.role': 'Staff role',
  'staff.active': 'Staff access',
  'user.password_reset': 'Password reset',
  'user.invite_resent': 'Invitation resent',
  'plan.create': 'Plan created',
  'plan.price': 'Plan price',
  'plan.limits': 'Plan limits',
  'plan.update': 'Plan renamed',
  'plan.push': 'Pushed to Stripe',
  'plan.stripe_ids_cleared': 'Stripe ids cleared',
  'plan.retire': 'Plan retired',
  'plan.restore': 'Plan restored',
  'plan.reorder': 'Plans reordered',
  'site.release_create': 'Release published',
  'site.release_update': 'Release edited',
  'site.release_delete': 'Release deleted',
  'site.faq_create': 'FAQ added',
  'site.faq_update': 'FAQ edited',
  'site.faq_delete': 'FAQ deleted',
  'site.publish': 'Site published',
  'site.shot_create': 'Screenshot added',
  'site.shot_update': 'Screenshot saved',
  'marketing.campaign_create': 'Campaign created',
  'marketing.campaign_update': 'Campaign edited',
  'marketing.campaign_start': 'Campaign started',
  'marketing.campaign_pause': 'Campaign paused',
  'marketing.campaign_delete': 'Campaign deleted',
  'marketing.suppression_remove': 'Bounced address cleared',
  'community.remove': 'Community post removed',
  'community.lock': 'Community thread locked',
  'marketing.recipients_add': 'Recipients added',
  'platform.settings': 'Services',
  'platform.storage': 'Storage',
  'platform.rules': 'Rules',
  'support.write': 'Support change',
  masquerade: 'Masquerade',
};

/** Money is amber, access is purple, destruction is red, support is quiet. */
export const ACTION_INTENT: Record<string, string> = {
  'account.plan': 'warn',
  'account.billing': 'warn',
  'account.price': 'warn',
  'account.free_period': 'warn',
  'account.credit': 'warn',
  'account.discount': 'warn',
  'account.billing_email': 'warn',
  'account.trial_grant': 'warn',
  'account.trial_update': 'warn',
  'account.trial_revoke': 'warn',
  'account.trial_end': 'warn',
  'account.moved_to_free': 'danger',
  'account.reconciled': 'warn',
  'account.status': 'caution',
  'account.create': 'ok',
  'account.delete': 'danger',
  'account.delete_scheduled': 'danger',
  'account.restore': 'ok',
  'account.user_add': 'info',
  'staff.create': 'identity',
  'staff.role': 'identity',
  'staff.active': 'identity',
  'plan.create': 'ok',
  'plan.price': 'warn',
  'plan.limits': 'warn',
  'plan.push': 'warn',
  'plan.stripe_ids_cleared': 'caution',
  'plan.retire': 'danger',
  'site.release_create': 'ok',
  'site.release_delete': 'danger',
  'site.faq_create': 'ok',
  'site.faq_delete': 'danger',
  'site.publish': 'info',
  'site.shot_create': 'ok',
  'marketing.campaign_create': 'ok',
  'marketing.campaign_start': 'info',
  'marketing.campaign_pause': 'caution',
  'marketing.campaign_delete': 'danger',
  'community.remove': 'danger',
  'community.lock': 'caution',
  'platform.settings': 'info',
  'platform.storage': 'info',
  'platform.rules': 'warn',
};

interface AuditChange { from: unknown; to: unknown }

/** A recorded move, if this key holds one. */
function moved(detail: Record<string, unknown>, key: string): AuditChange | null {
  const v = detail?.[key];
  return v && typeof v === 'object' && 'to' in (v as object) ? (v as AuditChange) : null;
}

const shown = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));
const arrow = (c: AuditChange) => `${shown(c.from)} → ${shown(c.to)}`;
/**
 * A stored date as the day it names — the trail prints days as YYYY-MM-DD. A
 * full timestamp is an instant, and is the day it falls on where the reader
 * is (review finding 81): a free period ending 9pm Eastern on the 14th was
 * printed as the 15th, the UTC day, beside a dialog that said the 14th.
 */
const day = (v: unknown) => (typeof v !== 'string' || !v ? '—' : v.length > 10 ? localDay(v) : v.slice(0, 10));
/** An instant as the day it falls on where the reader is (074's deletion time is a moment, not a day). */
const instantDay = (v: unknown) => (typeof v === 'string' && v ? localDay(v) : '—');
const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const obj = (v: unknown) => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null);

/** A site document's field path in the console's words: "home.pillars.1.title" → "Home › pillar 2". */
const SITE_FIELD: Record<string, string> = {
  eyebrow: 'announcement', eyebrowLink: 'announcement link', h1: 'headline', lede: 'intro', sub: 'intro',
  cta1: 'main button', cta2: 'second button', hero: 'screenshot',
  accent: 'accent', font: 'headings', radius: 'corners', favicon: 'favicon',
};
function sitePathLabel(path: string): string {
  const [page, key, index] = path.split('.');
  if (page === 'look') return `Look › ${SITE_FIELD[key] || key}`;
  if (page === 'home' && key === 'pillars') return `Home › pillar ${Number(index) + 1}`;
  if (page === 'features' && key === 'rows') return `Features › section ${Number(index) + 1}`;
  return `${page === 'home' ? 'Home' : page === 'features' ? 'Features' : page} › ${SITE_FIELD[key] || key}`;
}
/** "15% off for 12 months" / "15% off, forever" — the coupon's own name (D4). */
const percentWords = (pct: unknown, months: unknown) => {
  const m = typeof months === 'number' && months > 0 ? months : null;
  return `${shown(pct)}% off${m ? ` for ${count(m, 'month')}` : ', forever'}`;
};
/** A money write the server could not put on the Stripe bill (Stripe off). */
const localOnly = (d: Record<string, unknown>) => (d.applied === false ? ' — recorded here, not on a Stripe bill' : '');

/**
 * A support visit's access and reason (074): " — read-only — “Ticket #4702:
 * logo missing” · #4702". Empty for a visit from before 074, which recorded
 * neither.
 */
function supportVisitWords(d: Record<string, unknown>): string {
  const access = d.access === 'read' ? 'read-only' : d.access === 'full' ? 'full access' : '';
  const reason = typeof d.reason === 'string' && d.reason ? `“${d.reason}”` : '';
  const ref = typeof d.ref === 'string' && d.ref ? ` · ${d.ref}` : '';
  return [access, reason].filter(Boolean).map((w) => ` — ${w}`).join('') + (reason ? ref : '');
}

/**
 * The platform services, as the Services page names its tabs — and the
 * bounce mailbox, the Email tab's second card (tracking, 072).
 */
/** The rules' short names for the trail's sentence (the page's labels, shortened). */
const RULE_WORD: Record<string, string> = {
  firstTrialDays: 'first trial (days)', dunningGraceDays: 'declined-card grace (days)',
  dunningReminderDaysBefore: 'declined-card reminder (days before)', trialReminderDaysBefore: 'free period reminder (days before)',
  enforcePaidPlansWithTestKey: 'enforce paid plans with a test key', deleteGraceDays: 'deletion grace (days)',
  domainsPerAccount: 'custom domains per account', minPasswordLength: 'minimum password length',
  emailHourlyCap: 'outbox sends per hour', emailDailyCap: 'outbox sends per day', apiRequestsPerMinute: 'API requests per minute',
  maxFreePeriodDays: 'longest free period (days)', maxDiscountPercent: 'largest percent off',
  minAgreedPricePercent: 'lowest agreed price (% of the standard price)',
  pruneHoldDays: 'records kept after a move to Free (days)',
  staffFullAccessSupport: 'staff may enter support mode with full access',
  staffTwoFactorRequired: 'staff must use two-factor authentication',
  fileLinkDays: 'file links open without a sign-in (days)', fileLinksEnforced: 'enforce the file link lifetime',
  emailTransactionalDailyCap: 'form email per day, all accounts', emailAccountDailyCap: 'form email per day, one account',
  emailStepRecipients: 'addresses on one email', followUpReminderAttempts: 'follow-up reminder attempts',
};

const SERVICE_NAME: Record<string, string> = {
  stripe: 'Stripe', smtp: 'Email', captcha: 'Captcha', render: 'Custom domains', ai: 'AI', urls: 'URLs',
  bounceMailbox: 'Bounce mailbox',
};

function serviceSentence(d: Record<string, unknown>): string {
  const key = Object.keys(SERVICE_NAME).find((k) => obj(d[k]));
  if (!key) return 'Platform services changed';
  const v = obj(d[key])!;
  const name = SERVICE_NAME[key];
  if (v.cleared) return `${name} settings cleared`;
  const s = (x: unknown) => String(x);
  const parts: (string | false)[] = {
    stripe: [!!v.keyChanged && 'new secret key', !!v.webhookSecretChanged && 'new webhook secret'],
    smtp: [!!v.host && `${s(v.host)}:${s(v.port ?? 587)}`, !!v.passChanged && 'new password'],
    captcha: [!!v.siteKey && `site key ${s(v.siteKey)}`, !!v.secretChanged && 'new secret'],
    render: [!!v.serviceId && `service ${s(v.serviceId)}`, !!v.cnameTarget && `CNAME ${s(v.cnameTarget)}`, !!v.apiKeyChanged && 'new API key'],
    ai: [!!v.model && `model ${s(v.model)}`, !!v.apiKeyChanged && 'new API key'],
    urls: [!!v.publicBaseUrl && `public ${s(v.publicBaseUrl)}`, !!v.appBaseUrl && `app ${s(v.appBaseUrl)}`],
    bounceMailbox: [!!v.host && `${s(v.host)}:${s(v.port ?? 993)}`, !!v.useSmtpLogin && 'the SMTP login', !!v.passChanged && 'new password'],
  }[key] || [];
  const what = parts.filter(Boolean);
  return `${name} settings saved${what.length ? ` — ${what.join(', ')}` : ''}`;
}

/**
 * One entry as a sentence someone can act on, without opening the JSON.
 *
 * Only an entry about an account can read "a deleted account": the copied
 * name is gone only when the account was. A plan, a campaign, a service or a
 * staff member is named by what the entry itself recorded.
 */
export function describeAudit(e: PlatformAuditEntry): string {
  const d = e.detail || {};
  const where = e.account_name || 'a deleted account';
  const who = e.target_email || 'a deleted user';
  switch (e.action) {
    case 'account.create':
      return `${where} — ${shown(d.plan)}, ${shown(d.status)}`;
    case 'account.delete':
      // 074: the purge after the 30 days (by "system"), or a Super Admin's Delete now.
      return `${where} and everything in it${d.early ? ', before its 30 days were up' : ''}`;
    case 'account.delete_scheduled':
      return `${where} — deletes on ${instantDay(d.deleteAfter)} unless restored`;
    case 'account.restore':
      return `${where} — restored${d.status ? ` as ${shown(d.status).replace('_', ' ')}` : ''}`;
    case 'account.note':
      return `${where}: a staff note added`;
    case 'account.rename': {
      const parts = ['name', 'slug'].map((k) => { const c = moved(d, k); return c ? `${k} ${arrow(c)}` : ''; });
      return `${where}: ${parts.filter(Boolean).join(', ')}`;
    }
    case 'account.status':
    case 'account.plan': {
      const c = moved(d, e.action === 'account.status' ? 'status' : 'plan');
      // One discount at a time (Ben, 2026-09-26): a new plan or interval ends the percent-off.
      const ended = obj(d.percentEnded);
      // …and says so when Stripe did not take it off the bill.
      const still = d.percentStillOnBill === true ? ', but Stripe still has it on their bill' : '';
      return `${where}: ${c ? arrow(c) : '—'}${ended ? `; ${percentWords(ended.percent, ended.months)} ended${still}` : ''}`;
    }
    // PR 2: the Change plan dialog's one change — the terms it set, the terms
    // before, and what the server said happened (the toast's own facts).
    case 'account.billing': {
      const ended = obj(d.discountsEnded);
      const parts = [
        typeof d.chargedNowCents === 'number' ? `${money(d.chargedNowCents)} charged today` : '',
        d.when === 'renewal' ? 'from their next renewal' : d.when === 'now_bill_at_renewal' ? 'billed at their renewal' : '',
        ended && ended.percent != null ? `their ${shown(ended.percent)}% off ended`
          : ended && ended.agreedCents != null ? 'their agreed price ended' : '',
        d.freePeriodChange === 'ended' ? 'the free period ended' : '',
        d.via === 'local' ? 'recorded here, not at Stripe' : '',
      ].filter(Boolean);
      return `${where}: ${shown(d.summary)}${d.was ? ` (was ${shown(d.was)})` : ''}${parts.length ? ` — ${parts.join('; ')}` : ''}`;
    }
    case 'account.price': {
      const price = moved(d, 'priceOverrideCents');
      const interval = moved(d, 'billingInterval');
      const note = moved(d, 'priceOverrideNote');
      if (!d.negotiated) {
        return `${where}: back to the list price${price ? ` (was ${money(price.from as number)})` : ''}`;
      }
      const parts = [
        price ? `${money(price.to as number)} agreed` : '',
        interval ? `billed ${shown(interval.to) === 'year' ? 'annually' : 'monthly'}` : '',
      ].filter(Boolean);
      return `${where}: ${parts.join(', ') || 'agreed price unchanged'}${note?.to ? ` — ${note.to}` : ''}`;
    }
    case 'account.free_period': {
      const until = moved(d, 'freeUntil');
      const reason = moved(d, 'freeReason');
      if (!d.granted) return `${where}: free period ended${until ? ` (was ${shown(until.from)})` : ''}`;
      const when = until ? shown(until.to) : 'unchanged dates';
      return `${where}: free until ${when}${reason?.to ? ` — ${reason.to}` : ''}`;
    }
    // D5: a credit is days of the plan, off the next invoice, named by its reason.
    case 'account.credit': {
      if (d.days === null || d.days === undefined) return `${where}: credit withdrawn${localOnly(d)}`;
      const worth = typeof d.amountOffCents === 'number' ? ` (${money(d.amountOffCents)} off the next invoice)` : '';
      return `${where}: ${count(num(d.days), 'day')} credit${worth}${d.reason ? ` — ${shown(d.reason)}` : ''}${localOnly(d)}`;
    }
    // D4: the running percent-off, beside the credit — never an agreed price (one discount at a time).
    case 'account.discount': {
      const removed = obj(d.removed);
      if (removed) return `${where}: ${percentWords(removed.percent, removed.months)} removed`;
      const replaced = obj(d.replaced);
      const ends = typeof d.endsAt === 'string' && d.endsAt ? `, until ${day(d.endsAt)}` : '';
      return `${where}: ${percentWords(d.percent, d.months)}${ends}`
        + `${replaced ? ` (was ${percentWords(replaced.percent, replaced.months)})` : ''}`
        + `${d.note ? ` — ${shown(d.note)}` : ''}${localOnly(d)}`;
    }
    case 'account.billing_email': {
      const c = moved(d, 'billingEmail');
      if (!c) return `${where}: billing email unchanged`;
      if (!c.to) return `${where}: billing email cleared (was ${shown(c.from)})`;
      return `${where}: billing email ${c.from ? `${shown(c.from)} → ` : 'set to '}${shown(c.to)}`;
    }
    case 'account.trial_grant':
      return `${where}: ${shown(d.plan)} free until ${day(d.expiresAt)}, then ${d.autoConvert ? 'it converts' : 'it ends without a charge'}`;
    case 'account.trial_update': {
      const until = moved(d, 'expiresAt');
      const convert = moved(d, 'autoConvert');
      const parts = [
        until ? `free period now ends ${day(until.to)} (was ${day(until.from)})` : '',
        convert && convert.to === false ? 'it will end without a charge' : '',
      ].filter(Boolean);
      // Stripe is written only while it holds the period; one that stood locally is left alone.
      const untouched = d.stripe === 'untouched' ? ' — nothing changed at Stripe' : '';
      return `${where}: ${parts.join('; ') || 'free period changed'}${untouched}`;
    }
    // PR 2: a period whose end had passed, settled by the sweep (System).
    case 'account.trial_end': {
      const what = `the ${shown(d.planName ?? d.plan)} free period`;
      if (d.outcome === 'converted') return `${where}: ${what} converted ${day(d.expiresAt)} — Stripe billed on past its end`;
      if (d.why === 'subscription_ended') return `${where}: ${what} ended ${day(d.expiresAt)} with their subscription`;
      return `${where}: ${what} ended ${day(d.expiresAt)} — nothing was charged`;
    }
    // Ben, 2026-09-27: a paid plan is paid for — moved to Free by the sweep
    // (nothing paid, or the grace ended), or when Stripe ended a declining one.
    case 'account.moved_to_free':
      return `${where}: moved to Free from ${shown(d.fromName ?? d.from)} — ${d.why === 'first_payment_failed'
        ? 'its first payment failed, and a subscription that has never paid gets no grace'
        : d.why !== 'payment_failed'
        ? 'nothing was billing it'
        : d.by === 'stripe'
          ? 'Stripe ended the subscription while the card was declining'
          : `the card was still declined when its ${shown(d.graceDays ?? 15)}-day grace ended`}`;
    // The billing sweep asked Stripe and found an event the webhook never received.
    case 'account.reconciled':
      return `${where}: billing matched to Stripe — ${Array.isArray(d.found) ? d.found.map(shown).join('; ') : 'the record was behind'}`;
    case 'account.trial_revoke':
      return `${where}: free period ended early${d.note ? ` — ${shown(d.note)}` : ''}`;
    case 'account.portal_link':
      return `${where}: Billing Portal link emailed${d.emailedTo ? ` to ${shown(d.emailedTo)}` : ''}`;
    case 'account.user_add':
      return `${who} added to ${where}${d.seeded ? ' as its first login' : ''}`;
    case 'staff.create':
      return `${who} as ${PLATFORM_ROLE_LABEL[d.platformRole as PlatformRole] || shown(d.platformRole)}`;
    case 'staff.role': {
      const c = moved(d, 'platformRole');
      const label = (v: unknown) => PLATFORM_ROLE_LABEL[v as PlatformRole] || shown(v);
      return `${who}: ${c ? `${label(c.from)} → ${label(c.to)}` : '—'}`;
    }
    case 'staff.active':
      return `${who} ${d.active ? 'reactivated' : 'deactivated'}`;
    // Which of the two it was matters: a link only the user can use, or a
    // password a member of staff now knows.
    case 'user.password_reset':
      return `${who} — ${d.how === 'link' ? 'reset link emailed' : 'password set by staff'}`;
    case 'user.invite_resent':
      return `${who} — ${d.how === 'emailed' ? 'invitation emailed again' : 'new setup link handed over'}`;
    // Ben, 2026-10-09: every save a full-access support visit makes in the
    // customer's account. What changed is in the customer's own log.
    case 'support.write':
      return `in ${where}: saved through ${shown(d.method)} ${shown(d.path)}`;
    case 'masquerade':
      // 074: how they went in and why; a visit from before 074 has neither.
      return `into ${where}${supportVisitWords(d)}`;
    case 'plan.create':
    case 'plan.retire':
    case 'plan.restore':
      return `the ${shown(d.key)} plan`;
    case 'plan.price': {
      const c = moved(d, 'priceMonthlyCents');
      const pct = moved(d, 'annualDiscountPct');
      const parts = [
        c ? `${money(c.from as number)} → ${money(c.to as number)} a month` : '',
        pct ? `annual discount ${arrow(pct)}%` : '',
      ].filter(Boolean);
      return `${shown(d.key)}: ${parts.join(', ')}`;
    }
    case 'plan.limits':
      return `${shown(d.key)}: ceilings changed`;
    // The push is where a price reaches subscribers, so the sentence says how many moved.
    case 'plan.push': {
      const n = num(d.migrated);
      const prices = Array.isArray(d.changes)
        ? (d.changes as unknown[]).map((c) => obj(c)).filter(Boolean)
          .map((c) => `${money(num(c!.amountCents))}/${c!.interval === 'year' ? 'yr' : 'mo'}`)
        : [];
      const how = d.migration === 'grandfather' ? 'existing subscribers keep their price'
        : d.migration === 'at_renewal' ? `${count(n, 'account')} move at renewal`
          : d.migration === 'now_prorated' ? `${count(n, 'account')} moved now, prorated` : '';
      return `${shown(d.key)} pushed to Stripe${prices.length ? ` at ${prices.join(', ')}` : ''}${how ? ` — ${how}` : ''}`;
    }
    case 'plan.stripe_ids_cleared':
      return `${shown(d.key)}: Stripe ids cleared${d.product_id ? ` (was ${shown(d.product_id)})` : ''} — the next push builds it afresh`;
    // The order is the upgrade ladder, so the new order IS the change — the
    // old one is in the entry before this, and naming both would be noise.
    case 'plan.reorder':
      return Array.isArray(d.to) ? (d.to as unknown[]).map(shown).join(' → ') : '—';
    case 'site.release_create':
    case 'site.release_update':
    case 'site.release_delete':
      return `v${shown(d.version)} — ${shown(d.title)}`;
    case 'site.faq_create':
    case 'site.faq_update':
    case 'site.faq_delete':
      return `“${shown(d.question)}”`;
    // The publish records every field it changed ("home.h1", "look.accent");
    // the sentence names the first few in the console's words.
    case 'site.publish': {
      const paths = Array.isArray(d.paths) ? (d.paths as unknown[]).map(String) : [];
      const named = [...new Set(paths.map(sitePathLabel))];
      const n = num(d.changes) || paths.length;
      return `${count(n, 'change')}${named.length ? `: ${named.slice(0, 4).join(', ')}${named.length > 4 ? '…' : ''}` : ''}`;
    }
    case 'site.shot_create':
      return `${shown(d.file)} added to the library${d.phone ? ', with a phone crop' : ''}`;
    case 'site.shot_update': {
      const what = [
        d.desktop ? 'desktop image replaced' : '',
        d.phone === 'added' ? 'phone crop added' : d.phone === 'replaced' ? 'phone crop replaced' : d.phone === 'removed' ? 'phone crop removed' : '',
        d.alt ? 'alt text' : '',
        d.url ? 'browser bar text' : '',
      ].filter(Boolean);
      return `${shown(d.file)}${what.length ? `: ${what.join(', ')}` : ''}`;
    }
    case 'plan.update': {
      const c = moved(d, 'name');
      return `${shown(d.key)}${c ? `: ${arrow(c)}` : ''}`;
    }
    case 'marketing.campaign_create':
      return `“${shown(d.name)}” drafted from the ${shown(d.template)} template`;
    case 'marketing.campaign_update': {
      const fields = Array.isArray(d.fields) ? (d.fields as unknown[]).map(shown) : [];
      return `campaign #${shown(d.campaignId)}: ${fields.length ? `${fields.join(', ')} changed` : 'edited'}`;
    }
    case 'marketing.campaign_start':
      return `“${shown(d.name)}” started sending`;
    case 'marketing.campaign_pause':
      return `“${shown(d.name)}” paused`;
    case 'marketing.campaign_delete':
      return `“${shown(d.name)}”${d.status ? ` (it was ${shown(d.status)})` : ''}`;
    case 'marketing.suppression_remove':
      return `${shown(d.email)} taken off the bounce list — campaigns will try it again`;
    case 'community.remove':
      return d.what === 'thread'
        ? `the thread “${shown(d.title)}” by ${shown(d.author)}, with its replies`
        : `a reply by ${shown(d.author)} in thread #${shown(d.threadId)}`;
    case 'community.lock':
      return `“${shown(d.title)}” ${d.locked === false ? 'reopened' : 'locked'}`;
    case 'marketing.recipients_add': {
      const extra = [
        num(d.alreadyOnList) ? `${num(d.alreadyOnList)} already on the list` : '',
        num(d.invalid) ? `${count(num(d.invalid), 'unusable row')}` : '',
      ].filter(Boolean);
      return `campaign #${shown(d.campaignId)}: ${count(num(d.added), 'address', 'addresses')} added${extra.length ? `, ${extra.join(', ')}` : ''}`;
    }
    case 'platform.settings':
      return serviceSentence(d);
    // The numbers the service runs on (Platform › Rules): each move as "from → to".
    case 'platform.rules': {
      const changed = Array.isArray(d.changed) ? (d.changed as { key: string; from: unknown; to: unknown }[]) : [];
      const moves = changed.map((c) => `${RULE_WORD[c.key] || c.key} ${shown(c.from)} → ${shown(c.to)}`);
      if (d.reset) return `Platform rules reset to the defaults${moves.length ? ` — ${moves.join(', ')}` : ''}`;
      return moves.length ? `Platform rules changed — ${moves.join(', ')}` : 'Platform rules saved';
    }
    case 'platform.storage': {
      const set = obj(d.set);
      if (set) return `Upload storage set to the ${shown(set.bucket)} bucket${set.endpoint ? ` at ${shown(set.endpoint)}` : ''}`;
      if (d.migrationStarted) return 'Copying every stored file into the bucket started';
      if (d.cleared) return 'Upload storage settings cleared';
      return 'Upload storage changed';
    }
    default:
      // A newer server's action this build does not know. Only an account
      // entry may fall back to "a deleted account".
      if (e.action.startsWith('account.')) return where;
      return e.account_name || e.target_email || '—';
  }
}

/**
 * The same sentence on the account's own page (074), where naming the account
 * again is noise: "Kestrel Legal: active → suspended" reads "active →
 * suspended", and a support visit reads "Entered read-only — “reason”".
 */
export function describeForAccount(e: PlatformAuditEntry): string {
  if (e.action === 'masquerade') {
    const d = e.detail || {};
    const how = d.access === 'read' ? 'Entered read-only' : d.access === 'full' ? 'Entered with full access' : 'Entered in support mode';
    const reason = typeof d.reason === 'string' && d.reason ? ` — “${d.reason}”` : '';
    const ref = reason && typeof d.ref === 'string' && d.ref ? ` · ${d.ref}` : '';
    return `${how}${reason}${ref}`;
  }
  const sentence = describeAudit(e);
  const name = e.account_name;
  if (!name) return sentence;
  for (const lead of [`${name}: `, `${name} — `]) {
    if (sentence.startsWith(lead)) {
      const rest = sentence.slice(lead.length);
      return rest.charAt(0).toUpperCase() + rest.slice(1);
    }
  }
  return sentence;
}

const when = (iso: string) => new Date(iso).toLocaleString();
const roleAtTheTime = (e: PlatformAuditEntry) => (e.actor_role ? `${PLATFORM_ROLE_LABEL[e.actor_role]} at the time` : '');

export function ActionPill({ action }: { action: string }) {
  return <span className={`fs-pill ${ACTION_INTENT[action] || 'neutral'}`}>{ACTION_LABEL[action] || action}</span>;
}

/**
 * The trail itself: four columns on a desktop (When · What · Detail · Who,
 * "{Role} at the time" under the email), stacked entries on a phone — a
 * branch rendered only while PHONE matches (§4I I2). `describe` words the
 * Detail column; the account page passes `describeForAccount`.
 */
export function AuditTable({ entries, phone, describe = describeAudit }: {
  entries: PlatformAuditEntry[];
  phone: boolean;
  describe?: (e: PlatformAuditEntry) => string;
}) {
  if (phone) {
    return (
      <div className="pl-audit-cards">
        {entries.map((e) => (
          <div className="pl-audit" key={e.key}>
            <div className="top">
              <ActionPill action={e.action} />
              <span className="when">{when(e.created_at)}</span>
            </div>
            <div className="what">{describe(e)}</div>
            <div className="who">
              {actorName(e.actor_email)}
              {e.actor_role && ` · ${roleAtTheTime(e)}`}
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <table className="fs-table">
      <thead>
        <tr><th>When</th><th>What</th><th>Detail</th><th>Who</th></tr>
      </thead>
      <tbody>
        {entries.map((e) => (
          <tr key={e.key}>
            <td style={{ whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>{when(e.created_at)}</td>
            <td style={{ whiteSpace: 'nowrap' }}><ActionPill action={e.action} /></td>
            <td>{describe(e)}</td>
            <td style={{ whiteSpace: 'nowrap' }}>
              <div>{actorName(e.actor_email)}</div>
              {e.actor_role && <div className="fs-cell-sub faint">{roleAtTheTime(e)}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * "Recent platform activity" — the last 25 (or 100) entries at the foot of
 * the Accounts page, and the way into the whole log.
 */
export function AuditSection({ reloadKey, onError }: { reloadKey: number; onError: (m: string) => void }) {
  const phone = useMediaQuery(PHONE);
  const [entries, setEntries] = useState<PlatformAuditEntry[] | null>(null);
  const [limit, setLimit] = useState(25);

  useEffect(() => {
    listPlatformAudit(limit).then((r) => setEntries(r.entries)).catch((e) => onError(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadKey, limit]);

  return (
    <TableCard
      className="pl-audit-card pl-audit-recent"
      title="Recent platform activity"
      hint="Plans, statuses, free periods, provisioning, staff roles and support masquerades. Append-only."
      action={(
        <div className="st-actions">
          <button className="fs-btn neutral sm" onClick={() => setLimit(limit === 25 ? 100 : 25)}>
            {limit === 25 ? 'Show more' : 'Show less'}
          </button>
          <Link className="pl-audit-open" to="/platform/audit">Open the audit log →</Link>
        </div>
      )}
    >
      {!entries ? <div className="st-card-body st-muted">Loading…</div> : entries.length === 0 ? (
        <div className="st-card-body st-muted st-empty">
          Nothing recorded yet — plan, status, free-period and staff changes will appear here.
        </div>
      ) : <AuditTable entries={entries} phone={phone} />}
    </TableCard>
  );
}

/** The area chips, in the order the sidebar's sections run. */
export const AUDIT_AREAS: { value: PlatformAuditArea | null; label: string }[] = [
  { value: null, label: 'All' },
  { value: 'accounts', label: 'Accounts' },
  { value: 'billing', label: 'Billing' },
  { value: 'plans', label: 'Plans' },
  { value: 'staff', label: 'Staff' },
  { value: 'site', label: 'Site' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'services', label: 'Services' },
  { value: 'support', label: 'Support mode' },
];

/** `?area=` as the server knows it; anything else (or nothing) is All. */
const areaOf = (raw: string | null): PlatformAuditArea | null =>
  AUDIT_AREAS.find((a) => a.value && a.value === raw)?.value ?? null;

const PAGE = 50;

/**
 * `/platform/audit` — the whole platform trail (R12, sidebar "Audit"): every
 * staff action and every support-mode visit, newest first, one area at a
 * time if you like, fifty at a go with Load more on the server's cursor.
 */
export default function PlatformAuditPage() {
  const phone = useMediaQuery(PHONE);
  const [params, setParams] = useSearchParams();
  const area = areaOf(params.get('area'));
  const [entries, setEntries] = useState<PlatformAuditEntry[] | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [failed, setFailed] = useState(false);
  /** A reply to an area you have since left is dropped, not shown. */
  const asked = useRef(0);

  const load = useCallback(async (before: string | null) => {
    const ticket = ++asked.current;
    setBusy(true); setError('');
    try {
      const r = await listPlatformAudit(PAGE, { area, before });
      if (ticket !== asked.current) return;
      setEntries((cur) => (before && cur ? [...cur, ...r.entries] : r.entries));
      setNext(r.nextCursor ?? null);
      setFailed(false);
    } catch (e) {
      if (ticket !== asked.current) return;
      setError((e as Error).message || 'Could not load the audit log.');
      if (!before) setFailed(true);
    } finally {
      if (ticket === asked.current) setBusy(false);
    }
  }, [area]);

  useEffect(() => {
    setEntries(null); setNext(null); setFailed(false);
    void load(null);
  }, [load]);

  const pick = (value: PlatformAuditArea | null) => {
    if (value === area) return;
    setParams(value ? { area: value } : {}, { replace: true });
  };

  return (
    <div className="pl-page">
      <div className="fs-page-head pl-head">
        <div className="text">
          <h1>Platform audit log</h1>
          <div className="fs-page-sub">
            Everything staff did on the platform — accounts, billing, plans, staff, the public site, campaigns and
            services — and every support-mode visit, newest first. Append-only: nothing here can be edited or removed.
          </div>
        </div>
      </div>

      <div className="fs-seg pl-audit-areas" role="group" aria-label="Area">
        {AUDIT_AREAS.map((a) => {
          const on = a.value === area;
          return (
            <button key={a.label} type="button" className={on ? 'on' : ''} aria-pressed={on} onClick={() => pick(a.value)}>
              {a.label}
            </button>
          );
        })}
      </div>

      {error && <div className="ad-err">{error}</div>}

      <TableCard
        className="pl-audit-card"
        footer={next && entries && entries.length > 0 ? (
          <button className="fs-btn neutral sm" disabled={busy} onClick={() => void load(next)}>
            {busy ? 'Loading…' : 'Load more'}
          </button>
        ) : undefined}
      >
        {failed ? (
          <div className="st-card-body st-muted st-empty">
            The audit log could not be loaded.{' '}
            <button className="fs-btn neutral sm" disabled={busy} onClick={() => void load(null)}>Try again</button>
          </div>
        ) : !entries ? (
          <div className="st-card-body st-muted">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="st-card-body st-muted st-empty">
            {area ? 'Nothing recorded in this area yet.' : 'Nothing recorded yet.'}
          </div>
        ) : <AuditTable entries={entries} phone={phone} />}
      </TableCard>
    </div>
  );
}
